import { useEffect, useRef } from 'react';

function isEditableTarget(target) {
  if (!target || typeof target !== 'object') return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * Workspace tab shortcuts: Alt+T new tab, Alt+W close tab, Alt+[ / Alt+]
 * previous / next tab, Alt+1…6 jump to a tab. Browsers keep Cmd/Ctrl+T, W and
 * Ctrl+Tab for themselves, so those are left alone. Ignored while typing
 * (Alt+letter types characters on macOS); matched on e.code because on macOS
 * Alt changes e.key (Alt+T is "†").
 */
export function useWorkspaceTabShortcuts({ enabled = true, onNew, onClose, onNext, onPrev, onJump }) {
  const handlers = useRef({ onNew, onClose, onNext, onPrev, onJump });
  handlers.current = { onNew, onClose, onNext, onPrev, onJump };

  useEffect(() => {
    if (!enabled) return undefined;
    const onKeyDown = (e) => {
      if (e.defaultPrevented) return;
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (isEditableTarget(e.target)) return;
      const h = handlers.current;
      let handler = null;
      if (e.code === 'KeyT') handler = () => h.onNew?.();
      else if (e.code === 'KeyW') handler = () => h.onClose?.();
      else if (e.code === 'BracketLeft') handler = () => h.onPrev?.();
      else if (e.code === 'BracketRight') handler = () => h.onNext?.();
      else if (/^Digit[1-9]$/.test(e.code)) {
        const index = Number(e.code.slice(5)) - 1;
        handler = () => h.onJump?.(index);
      }
      if (!handler) return;
      e.preventDefault();
      handler();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
